import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';


/**
 * UndoToast الموحد (§1.4 و§1.6): يظهر أسفل الشاشة **ست ثوانٍ**، بحركة spring من
 * الأسفل، و`aria-live="polite"` كي يقرأه القارئ الصوتي بلا مقاطعة.
 *
 * المفردات على الـ toast من قاموس §5.10 حصرًا: «حُفظ» · «وُجّه» · «يحتاج مراجعة» ·
 * «بانتظار الإرسال ☁». الرسائل تصل من المستدعي، ونصوص الخطأ تأتي من `detail_ar`
 * كما أرسلها الخادم — لا تُصاغ رسالة خطأ في العميل (backend-api §8).
 */

const TOAST_MS = 6000;

export interface ToastAction {
  readonly label: string;
  readonly run: () => void | Promise<void>;
}

export interface ToastRequest {
  /** ReactNode لا string: اسم المشروع داخل الجملة يُعزل بـ `UserSpan` (§3.2 قاعدة 2). */
  readonly message: ReactNode;
  readonly tone?: 'default' | 'error';
  readonly action?: ToastAction;
}

interface ToastValue {
  readonly show: (request: ToastRequest) => void;
  /** المسار الأكثر استعمالًا: رسالة + «تراجع ↩» يستدعي `undo_action`. */
  readonly showUndo: (message: ReactNode, undo: () => void | Promise<void>) => void;
  readonly showError: (message: ReactNode) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

export function useToast(): ToastValue {
  const value = useContext(ToastContext);
  if (value === null) throw new Error('useToast خارج ToastProvider');
  return value;
}

interface ToastState extends ToastRequest {
  readonly key: number;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const counter = useRef(0);

  const show = useCallback((request: ToastRequest) => {
    counter.current += 1;
    setToast({ ...request, key: counter.current });
  }, []);

  const value = useMemo<ToastValue>(
    () => ({
      show,
      showUndo: (message, undo) => show({ message, action: { label: 'تراجع ↩', run: undo } }),
      showError: (message) => show({ message, tone: 'error' }),
    }),
    [show],
  );

  // المؤقّت يُصفَّر مع كل toast جديد؛ لا مؤقت دوري في التطبيق (§5.6-4).
  useEffect(() => {
    if (toast === null) return;
    const timer = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toast={toast} onDismiss={() => setToast(null)} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ toast, onDismiss }: { toast: ToastState | null; onDismiss: () => void }) {
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed bottom-0 start-0 end-0 z-[var(--z-toast)] flex justify-center px-4 pb-[calc(88px+env(safe-area-inset-bottom))] lg:pb-6"
    >
      {/* دخول بـ CSS (`animate-toast-in` في base.css): هذا المزوّد يغلّف الجذر،
          فمكتبة الحركة فيه تدخل الحزمة الابتدائية وتتجاوز ميزانية §7. الخروج
          المتحرك يُفقد بقصد — لا يستحق 31KB gz على أول تحميل. */}
      <>
        {toast !== null && (
          <div
            key={toast.key}
            role={toast.tone === 'error' ? 'alert' : 'status'}
            className={`animate-toast-in pointer-events-auto flex max-w-[min(30rem,100%)] items-center gap-3 rounded-md border bg-surface-raised px-4 py-3 text-body shadow-2 ${
              toast.tone === 'error' ? 'border-danger text-danger' : 'border-border text-text'
            }`}
          >
            <span className="min-w-0 flex-1">{toast.message}</span>
            {toast.action !== undefined && (
              <button
                type="button"
                onClick={() => {
                  const action = toast.action;
                  onDismiss();
                  if (action !== undefined) void action.run();
                }}
                className="shrink-0 rounded-sm px-2 py-1 text-caption font-semibold text-accent transition-colors duration-[var(--dur-1)] hover:bg-surface"
              >
                {toast.action.label}
              </button>
            )}
          </div>
        )}
      </>
    </div>
  );
}
